/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useState, useEffect } from "react";
import {
  ZendeskSsoTokenQuery,
  ZendeskSsoTokenData,
} from "../../graphql/zendesk-sso";
import { useLazyQuery } from "@apollo/react-hooks";
import { useHistory } from "react-router-dom";
import { Dimmer, Loader } from "semantic-ui-react";
import {
  authCheckCallApi,
  getUrlParam,
  setUrlPath,
} from "../Authenticator/utils";
import ConfirmationDialog from "../ConfirmationDialog";

interface SupportProps {
  plan: string;
  returnTo?: string;
}

const Support: React.FC<SupportProps> = ({ returnTo, plan }) => {
  const reportBuildKey = "appShield-reportBuild";
  const history = useHistory();
  const [error, setError] = useState(false);
  const [processingQuery, setProcessingQuery] = useState(true);
  const [getZendeskSsoToken] = useLazyQuery<ZendeskSsoTokenData>(
    ZendeskSsoTokenQuery,
    {
      onError: () => {
        setProcessingQuery(false);
        setError(true);
      },
      onCompleted: (ssoTokenData) => {
        if (!error) {
          redirectToSupport(ssoTokenData);
        }
      },
    }
  );

  useEffect(() => {
    if (plan !== "expired") {
      localStorage.setItem(reportBuildKey, getUrlParam("report"));
      authCheckCallApi(getZendeskSsoToken);
    }
  }, [getZendeskSsoToken, plan]);

  if (plan === "expired") {
    return (
      <ConfirmationDialog
        title={"Account expired"}
        message={"Please upgrade to access support."}
        onOk={() => {
          history.push("/");
        }}
      />
    );
  }

  const redirectToSupport = (ssoTokenData: any) => {
    if (
      !ssoTokenData ||
      !ssoTokenData.zendeskSsoToken ||
      !ssoTokenData.zendeskSsoToken.token
    ) {
      console.error("Invalid Zendesk JWT token");
      setError(true);
      return;
    }

    if (!ssoTokenData.zendeskSsoToken.ssoUrl) {
      console.error("Invalid Zendesk JWT token ssoUrl");
      setError(true);
      return;
    }

    let supportUrl = `${ssoTokenData.zendeskSsoToken.ssoUrl}/access/jwt?jwt=${ssoTokenData.zendeskSsoToken.token}`;
    if (returnTo) {
      // This is typically when we come _from_ Zendesk because he's asking us
      // to authenticate the user. In this case, we get here right after logging
      // on to PMA, and we should redirect to wherever ZD said we need to go to.
      supportUrl += "&return_to=" + encodeURIComponent(returnTo);
    } else if (ssoTokenData.zendeskSsoToken.landingPage) {
      // This is typically when the user clicks the Support link on PMA. In
      // this case, after the SSO authentication we need to go to the appropriate
      // landing page on ZD (which might be != the SSO token page).
      supportUrl +=
        "&return_to=" +
        encodeURIComponent(ssoTokenData.zendeskSsoToken.landingPage);

      const reportBuild = localStorage.getItem(reportBuildKey);
      if (reportBuild) {
        localStorage.removeItem(reportBuildKey);
        // Use different ticket_form_id params for production and non-production environments
        const formParams = window.location.hostname.includes("verimatrixcloud")
          ? "360001213533&360019850373"
          : "360001707414&360020124153";
        supportUrl += encodeURIComponent(
          `/requests/new?ticket_form_id=${formParams}=${reportBuild}`
        );
      }
    }
    // Finally, if no "return_to" is specified, Zendesk will just decide on its own
    // where to redirect the browser after the SSO has taken place.

    setUrlPath("/");
    window.location.href = supportUrl;
  };

  return (
    <React.Fragment>
      <Dimmer active={processingQuery}>
        <Loader active={processingQuery} size="large">
          Please wait...
        </Loader>
      </Dimmer>

      {error && (
        <ConfirmationDialog
          title={"Support"}
          message={
            "Unable to open support at this time, please try again later."
          }
          onOk={() => {
            history.push("/");
          }}
        />
      )}
    </React.Fragment>
  );
};

export default Support;
